import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  filter,
  first,
  map,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';

import { ToastService } from 'app/shared/services/toast.service';

import { EsriUtils } from 'app/shared/utils/esri.utils';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { generateTextSymbolDefault, measurement } from '../../default-symbols';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import Point from '@arcgis/core/geometry/Point';
import { FormatterUtils } from 'app/shared/utils/formatter.utils';
import {
  wkidLabelDictionary,
  WkIdType,
} from 'app/shared/constants/wkid.constant';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

interface WkIdOutItemLike {
  label: string;
  wkid: number;
  format?: (point: Point) => string;
}

@Component({
  selector: 'app-convert-coordinates',
  templateUrl: './convert-coordinates.component.html',
  styleUrls: ['./convert-coordinates.component.scss'],
})
export class ConvertCoordinatesComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  public mapView: __esri.MapView;

  @Input()
  public graphicsLayer: GraphicsLayer;

  wkidInItemList: CodedValueItem<number>[] = [
    WkIdType.WKID4326,
    WkIdType.WKID3763,
    WkIdType.WKID5016,
    WkIdType.WKID5014,
    WkIdType.WKID5015,
    WkIdType.WKID3857,
  ].map((value) => {
    const label = wkidLabelDictionary.get(value);
    return { label, value };
  });
  wkidOutItemList: WkIdOutItemLike[];

  wkidInSelected: number = this.wkidInItemList[0].value;
  wkidItemOutSelected: WkIdOutItemLike;

  inCoordsListText: string;
  outCoordsListText: string;

  hintText = this.translationService.getCurrentLanguage()['enter-coordinates'];
  helpHintTooltip = this.hintText.replaceAll('\n', '<br />');

  private graphicsCoordsList: __esri.Graphic[] = [];
  private graphicsTextList: __esri.Graphic[] = [];

  private putIntoMapGraphicsSubject = new Subject<Point[]>();
  private getPointFromMapSubject = new Subject<void>();

  private mapViewSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private mainMapService: MainMapService,
    private toastService: ToastService,
    private screenService: ScreenService,
    public translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.wkidOutItemList = [
      WkIdType.WKID3763,
      WkIdType.WKID5016,
      WkIdType.WKID5014,
      WkIdType.WKID5015,
      WkIdType.WKID3857,
    ].map((wkid) => {
      const label = wkidLabelDictionary.get(wkid);
      return { label, wkid };
    });

    this.wkidOutItemList.push(
      (() => {
        const wkid = WkIdType.WKID4326;
        const label = wkidLabelDictionary.get(wkid) + ' (DD)';
        return {
          label,
          wkid,
          format: (point) => {
            return [
              FormatterUtils.formatDD(point.y, 'lat'),
              FormatterUtils.formatDD(point.x, 'long'),
            ].join(', ');
          },
        };
      })()
    );

    this.wkidOutItemList.push(
      (() => {
        const wkid = WkIdType.WKID4326;
        const label = wkidLabelDictionary.get(wkid) + ' (DMS)';
        return {
          label,
          wkid,
          format: (point) => {
            return [
              FormatterUtils.formatDMS(point.y, 'lat'),
              FormatterUtils.formatDMS(point.x, 'long'),
            ].join(', ');
          },
        };
      })()
    );

    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.reset();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView || changes.graphicsLayer) {
      this.handleMapView();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.mapViewSubscription?.unsubscribe();

    this.wkidOutItemList = null;
  }

  onBtnPutIntoMapClick() {
    if (!this.wkidInSelected) {
      return;
    }

    const points: Point[] = [];

    if (this.inCoordsListText) {
      for (const text of this.inCoordsListText
        .split('\n')
        .map((x) => x.trim())
        .filter((x) => !!x)) {
        const yxArr = text
          .split(',')
          .map((x) => x.trim())
          .map((v) => Number(v));
        if (
          yxArr.length !== 2 ||
          yxArr.some((v) => Number.isNaN(v) || !Number.isFinite(v))
        ) {
          this.toastService.error(
            this.translationService.getCurrentLanguage()['invalid-coordinates']
          );

          return;
        }

        points.push(
          new Point({
            x: yxArr[1],
            y: yxArr[0],
            spatialReference: { wkid: this.wkidInSelected },
          })
        );
      }
    }

    this.putIntoMapGraphicsSubject.next(points);
  }

  onBtnProjectClick() {
    if (!this.wkidItemOutSelected) {
      return;
    }

    const pointsProjected: Point[] = [];
    if (this.graphicsCoordsList.length) {
      for (const graphic of this.graphicsCoordsList) {
        let point = graphic.geometry as Point;
        point = EsriUtils.project(point, {
          wkid: this.wkidItemOutSelected.wkid,
        });

        if (!point) {
          this.outCoordsListText = '';
          this.toastService.error(
            this.translationService.getCurrentLanguage()[
              'failed-project-coordinates'
            ]
          );
          return;
        }

        pointsProjected.push(point);
      }
    }

    this.outCoordsListText = pointsProjected
      .map((point) => {
        if (this.wkidItemOutSelected.format) {
          return this.wkidItemOutSelected.format(point);
        }

        return this.pointToStr(point);
      })
      .join('\n');
  }

  onBtnGetMapPointClick() {
    this.getPointFromMapSubject.next();
  }

  onBtnZoomToClick() {
    if (this.graphicsCoordsList.length) {
      const geometryList = this.graphicsCoordsList.map((x) => x.geometry);
      this.mainMapService.goTo(geometryList);
    }
  }

  onBtnClearClick() {
    this.reset();
  }

  private reset() {
    this.wkidInSelected = this.wkidInItemList[0].value;
    this.wkidItemOutSelected = null;

    this.inCoordsListText = '';
    this.outCoordsListText = '';

    this.mapViewSubscription?.unsubscribe();
    this.graphicsCoordsList = [];
    this.graphicsTextList = [];
    this.handleMapView();
  }

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();
    if (!this.mapView) {
      return;
    }

    const { mapView } = this;

    const graphicsCollection =
      this.graphicsLayer?.graphics ?? this.mapView.graphics;

    const removeAllCurrentPoints = () => {
      this.graphicsCoordsList.forEach((graphic) => {
        graphicsCollection.remove(graphic);
      });

      this.graphicsTextList?.forEach((graphic) => {
        graphicsCollection.remove(graphic);
      });
    };

    const subscription = new Subscription(removeAllCurrentPoints);

    const putIntoMapGraphicSubject = new Subject<__esri.Point>();

    subscription.add(
      putIntoMapGraphicSubject.subscribe((point) => {
        let graphic = new Graphic({
          geometry: point,
          symbol: measurement.pointSymbolDefault,
        });

        this.graphicsCoordsList.push(graphic);

        graphicsCollection.add(graphic);

        const xFormatted = FormatterUtils.formatNumber(point.x);
        const yFormatted = FormatterUtils.formatNumber(point.y);

        const coordsFormatted = [`X: ${xFormatted}`, `Y: ${yFormatted}`].join(
          '\n'
        );

        graphic = new Graphic({
          geometry: point,
          symbol: generateTextSymbolDefault(coordsFormatted, {
            boxWidth: 100,
            boxHeight: 30,
            offsetY:
              measurement.pointSymbolDefault.size / 2 +
              (measurement.pointSymbolDefault.outline?.width ?? 0) +
              3,
          }),
        });

        this.graphicsTextList.push(graphic);

        graphicsCollection.add(graphic);
      })
    );

    subscription.add(
      this.putIntoMapGraphicsSubject.subscribe((points) => {
        removeAllCurrentPoints();

        this.graphicsCoordsList = [];
        this.graphicsTextList = [];

        for (const point of points) {
          putIntoMapGraphicSubject.next(point);
        }

        this.mainMapService.goTo(points);
      })
    );

    subscription.add(
      this.getPointFromMapSubject
        .pipe(
          switchMap(() => {
            return EsriUtils.leftClicks(mapView).pipe(
              first(),
              map((e) => e.mapPoint as Point)
            );
          })
        )
        .subscribe((point) => {
          if (!this.inCoordsListText) {
            this.inCoordsListText = '';
          }

          if (this.inCoordsListText && !this.inCoordsListText.endsWith('\n')) {
            this.inCoordsListText += '\n';
          }

          if (this.wkidInSelected) {
            point = EsriUtils.project(point, { wkid: this.wkidInSelected });
          }

          this.inCoordsListText += this.pointToStr(point);

          putIntoMapGraphicSubject.next(point);
        })
    );

    this.mapViewSubscription = subscription;
  }

  private pointToStr(point: Point) {
    return FormatterUtils.formatPoint(point);
  }
}
